import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, Download, FileSpreadsheet, Upload, Loader2, Clock, Rows3, Columns3, Database, HardDrive } from 'lucide-react';
import { getSavedFiles, deleteSavedFile, loadSavedFile, downloadSavedFileCsv, downloadSavedFileExcel } from '../utils/api';
import { toast } from 'sonner';
import { useT } from '../i18n';

const SavedFiles = ({ onClose, onFileLoaded }) => {
  const t = useT();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxFiles, setMaxFiles] = useState(10);
  const [isPersistent, setIsPersistent] = useState(true);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const result = await getSavedFiles();
      setFiles(result.files || []);
      setMaxFiles(result.max_files || 10);
      setIsPersistent(result.is_persistent !== false);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;

    try {
      const result = await deleteSavedFile(fileId);
      toast.success(result.message);
      await loadFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('savedFiles.deleteFailed'));
    }
  };

  const handleLoad = async (fileId, filename) => {
    try {
      setLoading(true);
      const result = await loadSavedFile(fileId);
      toast.success(result.message);
      if (onFileLoaded) {
        onFileLoaded(result);
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('savedFiles.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (fileId, filename, format) => {
    const url = format === 'csv' 
      ? downloadSavedFileCsv(fileId) 
      : downloadSavedFileExcel(fileId);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content max-w-2xl" 
        onClick={(e) => e.stopPropagation()}
        data-testid="saved-files-modal"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{t('savedFiles.title')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t('savedFiles.count', { used: files.length, max: maxFiles })}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="btn-ghost h-8 w-8 p-0" 
              data-testid="close-saved-files"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Il n'y a plus rien à faire pour garder un fichier. */}
          <div className="mb-6 p-4 bg-muted/30 rounded-lg flex items-start gap-2.5">
            <Save className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground" data-testid="saved-files-automatic">
              {t('savedFiles.automatic', { max: maxFiles })}
            </p>
          </div>

          {/* Saved files list */}
          <div>
            <h3 className="text-sm font-medium mb-3">{t('savedFiles.yours')}</h3>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>{t('savedFiles.empty')}</p>
                <p className="text-sm">{t('savedFiles.emptyHint')}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {files.map((file) => (
                  <div 
                    key={file.file_id}
                    className="card p-4 hover:border-primary/50 transition-colors"
                    data-testid={`saved-file-${file.file_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate" title={file.filename}>
                          {file.filename}
                        </h4>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(file.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Rows3 className="h-3 w-3" />
                            {t('savedFiles.rows', { count: file.rows.toLocaleString() })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Columns3 className="h-3 w-3" />
                            {t('savedFiles.columns', { count: file.columns })}
                          </span>
                          {file.operations_count > 0 && (
                            <span className="badge badge-primary">
                              {t('savedFiles.operations', { count: file.operations_count })}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {/* Load button */}
                        <button
                          onClick={() => handleLoad(file.file_id, file.filename)}
                          className="btn-ghost h-8 w-8 p-0"
                          title={t('savedFiles.loadInto')}
                          data-testid={`load-file-${file.file_id}`}
                        >
                          <Upload className="h-4 w-4" />
                        </button>
                        
                        {/* Download CSV */}
                        <button
                          onClick={() => handleDownload(file.file_id, file.filename, 'csv')}
                          className="btn-ghost h-8 w-8 p-0"
                          title={t('savedFiles.downloadCsv')}
                          data-testid={`download-csv-${file.file_id}`}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(file.file_id, file.filename)}
                          className="btn-ghost h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                          title={t('savedFiles.deleteFile')}
                          data-testid={`delete-file-${file.file_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info note */}
          <div className={`mt-4 p-3 rounded-lg text-xs ${isPersistent ? 'bg-muted/30 text-muted-foreground' : 'bg-warning/10 text-warning-foreground border border-warning/30'}`}>
            {isPersistent ? (
              <p className="flex items-start gap-2">
                <Database className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{t('savedFiles.persistentLabel')}</strong>{' '}
                  {t('savedFiles.persistentBody', { max: maxFiles })}
                </span>
              </p>
            ) : (
              <p className="flex items-start gap-2">
                <HardDrive className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{t('savedFiles.temporaryLabel')}</strong>{' '}
                  {t('savedFiles.temporaryBody', { max: maxFiles })}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavedFiles;
